import { useMemo, useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import { EDITORIAL_QUEUE, EDITORIAL_STATUSES, regionName } from './adminData';
import type { EditorialItem, EditorialStatus } from './adminData';
import StatusBadge from './StatusBadge';
import type { BadgeTone } from './StatusBadge';
import EditorialDetail from './editorial/EditorialDetail';

type Filter = 'all' | EditorialStatus;

export function toneForStatus(status: EditorialStatus): BadgeTone {
  if (status === 'Published') return 'live';
  if (status === 'Approved') return 'done';
  if (status === 'Scheduled') return 'scheduled';
  if (status === 'Revisions requested') return 'alert';
  if (status === 'Needs review') return 'review';
  if (status === 'AI-prepared') return 'review';
  return 'draft';
}

/** Stages that need an editor's attention; shown first in the queue. */
const NEEDS_ACTION: EditorialStatus[] = ['Needs review', 'Revisions requested', 'AI-prepared', 'Incoming'];

/** Short labels so the whole pipeline fits on one row. */
const SHORT: Record<EditorialStatus, string> = {
  Incoming: 'Incoming',
  'AI-prepared': 'AI-prepared',
  'Needs review': 'Review',
  'Revisions requested': 'Revisions',
  Approved: 'Approved',
  Scheduled: 'Scheduled',
  Published: 'Published',
};

export default function EditorialInbox() {
  const [items, setItems] = useState<EditorialItem[]>(EDITORIAL_QUEUE);
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedId, setSelectedId] = useState<string>(EDITORIAL_QUEUE[0]?.id ?? '');
  const [mobileDetail, setMobileDetail] = useState(false);

  const counts = useMemo(() => {
    const out: Record<string, number> = { all: items.length };
    EDITORIAL_STATUSES.forEach((s) => { out[s] = items.filter((i) => i.status === s).length; });
    return out;
  }, [items]);

  const needsAction = useMemo(() => items.filter((i) => NEEDS_ACTION.includes(i.status)).length, [items]);

  // In "All", stories that need an editor come first, in pipeline order.
  const rows = useMemo(() => {
    const list = filter === 'all' ? [...items] : items.filter((i) => i.status === filter);
    const rank = (s: EditorialStatus) => {
      const a = NEEDS_ACTION.indexOf(s);
      return a === -1 ? 10 + EDITORIAL_STATUSES.indexOf(s) : a;
    };
    return list.sort((x, y) => rank(x.status) - rank(y.status));
  }, [items, filter]);

  const selected = useMemo(() => items.find((i) => i.id === selectedId) || null, [items, selectedId]);

  const updateItem = (id: string, patch: Partial<EditorialItem>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };

  const openItem = (id: string) => {
    setSelectedId(id);
    setMobileDetail(true);
  };

  return (
    <div className="space-y-5">
      {/* Pipeline: every stage in workflow order, with counts. Click a stage to filter. */}
      <div className="bg-background-50 border border-background-200 rounded-lg">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4">
          <p className="text-sm text-foreground-700">
            <span className="font-heading font-bold text-foreground-950">{needsAction}</span> {needsAction === 1 ? 'story needs' : 'stories need'} an editor
          </p>
          <DemoBadge label="Demo — sample queue" />
        </div>
        <div className="overflow-x-auto px-4 pb-4 pt-3">
          <div className="flex min-w-max items-stretch gap-1.5">
            <button
              type="button"
              onClick={() => setFilter('all')}
              aria-pressed={filter === 'all'}
              className={`flex flex-col items-start rounded-md border px-3 py-2 min-w-[84px] cursor-pointer transition-colors ${filter === 'all' ? 'bg-foreground-950 border-foreground-950 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-foreground-500'}`}
            >
              <span className="font-heading font-bold text-xl leading-none">{counts.all}</span>
              <span className="mt-1 font-label uppercase tracking-wide text-[10px] font-semibold">All</span>
            </button>
            {EDITORIAL_STATUSES.map((s, i) => (
              <div key={s} className="flex items-stretch gap-1.5">
                {i > 0 && <span className="self-center text-foreground-400 text-xs" aria-hidden="true"><i className="ri-arrow-right-s-line"></i></span>}
                <button
                  type="button"
                  onClick={() => setFilter(s)}
                  aria-pressed={filter === s}
                  title={s}
                  className={`flex flex-col items-start rounded-md border px-3 py-2 min-w-[84px] cursor-pointer transition-colors ${filter === s ? 'bg-foreground-950 border-foreground-950 text-background-50' : NEEDS_ACTION.includes(s) && counts[s] > 0 ? 'bg-accent-100 border-accent-300 text-foreground-900 hover:border-accent-600' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-foreground-500'}`}
                >
                  <span className="font-heading font-bold text-xl leading-none">{counts[s]}</span>
                  <span className="mt-1 font-label uppercase tracking-wide text-[10px] font-semibold whitespace-nowrap">{SHORT[s]}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* Queue list */}
        <div className={`xl:col-span-4 ${mobileDetail ? 'hidden xl:block' : ''}`}>
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <p className="px-4 py-3 border-b border-background-200 font-label uppercase tracking-widest text-[10px] font-bold text-foreground-600">
              {filter === 'all' ? 'All stories · needs action first' : `${filter} · ${rows.length}`}
            </p>
            <ul className="divide-y divide-background-200 max-h-[680px] overflow-y-auto">
              {rows.length === 0 && <li className="px-4 py-10 text-center text-sm text-foreground-500">Nothing in this stage.</li>}
              {rows.map((it) => (
                <li key={it.id}>
                  <button
                    type="button"
                    onClick={() => openItem(it.id)}
                    aria-current={selectedId === it.id}
                    className={`w-full text-left px-4 py-3.5 cursor-pointer transition-colors border-l-4 ${selectedId === it.id ? 'bg-background-100 border-l-primary-500' : 'border-l-transparent hover:bg-background-100'}`}
                  >
                    <p className="text-sm font-semibold text-foreground-950 leading-snug line-clamp-2">{it.form.headline}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <StatusBadge tone={toneForStatus(it.status)}>{it.status}</StatusBadge>
                      {it.aiPrepared && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-accent-800" title="AI draft prepared">
                          <i className="ri-sparkling-line"></i>AI draft
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-xs text-foreground-500">
                      {it.source} · {it.regionIds.map(regionName).join(', ') || 'No region'} · {it.received}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Detail */}
        <div className={`xl:col-span-8 min-w-0 ${mobileDetail ? '' : 'hidden xl:block'}`}>
          {selected ? (
            <EditorialDetail item={selected} onUpdate={(patch) => updateItem(selected.id, patch)} onBack={() => setMobileDetail(false)} />
          ) : (
            <div className="bg-background-50 border border-background-200 rounded-lg px-5 py-12 text-center text-sm text-foreground-500">
              Select a story from the queue to review it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
